import { useEffect, useState } from "react";
import { adminApi } from "../../lib/api";
import { Button } from "../../components/ui/button";
import {
  RefreshCcw, CheckCircle2, XCircle, Clock, Cloud, CloudOff,
  AlertTriangle, Zap, SkipForward, Play, Skull,
} from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key:"pending",  label:"Pending",  Icon:Clock,         accent:"#d97706" },
  { key:"failed",   label:"Failed",   Icon:XCircle,       accent:"#e31e24" },
  { key:"dead",     label:"Dead",     Icon:Skull,         accent:"#991b1b" },
  { key:"conflict", label:"Conflict", Icon:AlertTriangle, accent:"#b45309" },
  { key:"sent",     label:"Sent",     Icon:CheckCircle2,  accent:"#16a34a" },
  { key:"skipped",  label:"Skipped",  Icon:SkipForward,   accent:"#475569" },
  { key:"all",      label:"All",      Icon:null,          accent:"#1e5bb8" },
];

const EVENT_LABELS = {
  order_created:"Order created", order_status_changed:"Order status",
  credit_issued:"Credit issued", return_created:"Return",
  inventory_delta:"Stock delta", inventory_bulk:"Bulk stock",
  product_updated:"Product update", ebay_listing:"eBay listing",
};

const STATUS_STYLE = {
  pending:    { bg:"#fef3c7", color:"#92400e", border:"1px solid #fde68a" },
  processing: { bg:"#dbeafe", color:"#1e40af", border:"1px solid #93c5fd" },
  sent:       { bg:"#dcfce7", color:"#15803d", border:"1px solid #86efac" },
  failed:     { bg:"#fee2e2", color:"#991b1b", border:"1px solid #fca5a5" },
  dead:       { bg:"#fee2e2", color:"#7f1d1d", border:"1px solid #f87171" },
  conflict:   { bg:"#fffbeb", color:"#b45309", border:"1px solid #fcd34d" },
  skipped:    { bg:"#f1f5f9", color:"#475569", border:"1px solid #e2e8f0" },
};

export default function AdminSyncQueue() {
  const [tab,          setTab]          = useState("pending");
  const [data,         setData]         = useState({ items:[], stats:{} });
  const [pinnacle,     setPinnacle]     = useState(null);
  const [busy,         setBusy]         = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [last,         setLast]         = useState(null);

  // ── ALL PINNACLE LOGIC UNTOUCHED ─────────────────────────────────
  const load = async () => {
    try {
      const [{ data:q },{ data:p },{ data:l }] = await Promise.all([
        adminApi.get(`/admin/sync-queue${tab!=="all"?`?status=${tab}`:""}`),
        adminApi.get("/pinnacle/status"),
        adminApi.get("/admin/sync/last").catch(()=>({ data:{} })),
      ]);
      setData(q); setPinnacle(p); setLast(l);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  useEffect(() => {
    const poll = async () => {
      try {
        const { data } = await adminApi.post("/admin/sync/auto-check");
        if (data.auto_sync && (data.sent>0||data.flushed>0)) {
          toast.success(`Auto-sync: ${data.sent||data.flushed} event(s) delivered`);
          load();
        }
      } catch { /* ignore */ }
    };
    const t = setInterval(poll, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  const retry = async (id) => {
    await adminApi.post(`/admin/sync-queue/${id}/retry`);
    toast.success("Marked for retry"); load();
  };

  const processOne = async (id) => {
    setProcessingId(id);
    try {
      const { data } = await adminApi.post(`/admin/sync-queue/${id}/process`);
      if      (data.status==="sent")     toast.success("Event delivered to Pinnacle");
      else if (data.status==="skipped")  toast.info("Skipped — adapter not ready yet");
      else if (data.status==="conflict") toast.warning("Conflict detected — see Conflicts page");
      else                               toast.error(`Failed: ${data.error||"unknown"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Processing failed");
    } finally { setProcessingId(null); }
  };

  const syncNow = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/sync/now");
      const count = data.flushed ?? data.sent ?? 0;
      toast.success(`Sync complete — ${count} event(s) processed`);
      load();
    } catch { toast.error("Sync failed"); }
    finally { setBusy(false); }
  };
  // ── END PINNACLE LOGIC ────────────────────────────────────────────

  const stats = data.stats || {};

  return (
    <div className="space-y-5" data-testid="admin-sync-queue-page">

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] mb-1" style={{ color:"#94a3b8" }}>Sync</div>
          <h1 className="font-heading text-[28px] font-bold" style={{ color:"#0f172a" }}>Sync Queue</h1>
          <p className="text-[12px] mt-0.5" style={{ color:"#64748b" }}>
            Offline-first event log · Queue → Worker → Adapter · auto-check every 30s
          </p>
          {last?.last_sync_at && (
            <div className="text-[11px] font-mono mt-1" style={{ color:"#94a3b8" }} data-testid="last-sync-info">
              Last sync {new Date(last.last_sync_at).toLocaleString("en-AU")} · {last.last_sync_by}
            </div>
          )}
        </div>
        <Button onClick={syncNow} disabled={busy||(stats.pending||0)===0}
          className="h-9 rounded-xl text-white font-bold uppercase tracking-wide text-[13px]"
          style={{ background:"#e31e24" }} data-testid="sync-now-btn">
          <Zap className="h-4 w-4 mr-2"/>
          {busy ? "Syncing…" : `Sync Now${stats.pending>0?` (${stats.pending})`:""}`}
        </Button>
      </div>

      {/* Pinnacle status banner */}
      {pinnacle && (
        <div className="bg-white rounded-2xl p-4 flex items-center gap-3 shadow-sm"
          style={{
            border:`1px solid ${pinnacle.online?"#86efac":"#fca5a5"}`,
            borderLeft:`4px solid ${pinnacle.online?"#16a34a":"#e31e24"}`,
          }} data-testid="pinnacle-status-banner">
          {pinnacle.online
            ? <Cloud className="h-5 w-5 shrink-0" style={{ color:"#16a34a" }}/>
            : <CloudOff className="h-5 w-5 shrink-0" style={{ color:"#e31e24" }}/>
          }
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-[14px] uppercase" style={{ color:"#0f172a" }}>
              Pinnacle · {pinnacle.label}
            </div>
            <div className="text-[12px]" style={{ color:"#64748b" }}>
              Mode <span className="font-mono font-bold">{(pinnacle.mode||"csv").toUpperCase()}</span>
              {" · "}{pinnacle.online ? "Operating normally" : "Offline — queue accumulating locally"}
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap justify-end">
            {[
              { label:"pending",  value:stats.pending||0,  style:STATUS_STYLE.pending  },
              { label:"failed",   value:stats.failed||0,   style:STATUS_STYLE.failed   },
              { label:"dead",     value:stats.dead||0,     style:STATUS_STYLE.dead     },
              { label:"sent",     value:stats.sent||0,     style:STATUS_STYLE.sent     },
            ].map(({ label, value, style }) => (
              <span key={label} className="text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-0.5 rounded-lg"
                style={{ background:style.bg, color:style.color, border:style.border }}>
                {value} {label}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {TABS.map(({ key, label, Icon, accent }) => {
          const count = stats[key] ?? 0;
          return (
            <button key={key} onClick={() => setTab(key)}
              className="flex items-center gap-1.5 h-9 px-3 rounded-xl text-[11px] font-mono uppercase tracking-widest transition-all"
              style={tab===key
                ? { background:"#0f172a", color:"#ffffff" }
                : { background:"#f5f7fb", border:"1px solid #dde3ef", color:"#64748b" }
              } data-testid={`sync-tab-${key}`}>
              {Icon && <Icon className="h-3 w-3" style={{ color: tab===key ? "#ffffff" : accent }}/>}
              {label}
              {key!=="all" && count>0 && (
                <span className="text-[10px] font-bold" style={{ color: tab===key ? "#94a3b8" : accent }}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Dead letter warning */}
      {tab==="dead" && (stats.dead||0)>0 && (
        <div className="flex items-start gap-3 p-4 rounded-2xl"
          style={{ background:"#fee2e2", border:"1px solid #fca5a5" }}>
          <Skull className="h-5 w-5 shrink-0 mt-0.5" style={{ color:"#e31e24" }}/>
          <div>
            <div className="font-heading font-bold text-[14px] uppercase" style={{ color:"#991b1b" }}>Dead Letter Queue</div>
            <p className="text-[12px] mt-0.5" style={{ color:"#7f1d1d" }}>
              These events exhausted all 6 retry attempts. Resolve the root cause, then click <b>Retry</b>.
            </p>
          </div>
        </div>
      )}

      {/* Conflict warning */}
      {tab==="conflict" && (stats.conflict||0)>0 && (
        <div className="flex items-start gap-3 p-4 rounded-2xl"
          style={{ background:"#fffbeb", border:"1px solid #fcd34d" }}>
          <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" style={{ color:"#d97706" }}/>
          <div>
            <div className="font-heading font-bold text-[14px] uppercase" style={{ color:"#92400e" }}>Data Conflicts Detected</div>
            <p className="text-[12px] mt-0.5" style={{ color:"#78350f" }}>
              Paused due to data conflicts. Go to <b>Sync → Conflicts</b> to resolve before retrying.
            </p>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden overflow-x-auto" style={{ border:"1px solid #dde3ef" }}>
        <table className="w-full text-sm" style={{ minWidth:760 }}>
          <thead>
            <tr style={{ background:"#f5f7fb", borderBottom:"1px solid #dde3ef" }}>
              {["Event","Reference","Queued","Attempts","Next retry","Status","Actions"].map((h,i)=>(
                <th key={h} className="py-3 px-4 font-mono font-semibold uppercase"
                  style={{ color:"#475569", fontSize:10, letterSpacing:"0.15em", textAlign:i>=6?"right":"left" }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.items.map((it, idx) => (
              <tr key={it.id} className="hover:bg-[#f5f7fb] transition-colors"
                style={{ borderBottom: idx<data.items.length-1?"1px solid #f0f4f8":"none" }}
                data-testid={`queue-row-${it.id}`}>

                <td className="px-4 py-3">
                  <div className="font-medium text-[13px]" style={{ color:"#0f172a" }}>
                    {EVENT_LABELS[it.event_type]||it.event_type}
                  </div>
                  <div className="text-[9px] font-mono uppercase tracking-widest mt-0.5" style={{ color:"#94a3b8" }}>
                    {it.event_type}
                  </div>
                </td>

                <td className="px-4 py-3">
                  <div className="font-mono text-[12px]" style={{ color:"#0f172a" }}>
                    {it.ref_id ? it.ref_id.slice(0,12)+"…" : "—"}
                  </div>
                  <div className="text-[9px] font-mono" style={{ color:"#94a3b8" }}>{it.ref_type||""}</div>
                  {it.external_id && (
                    <div className="text-[9px] font-mono mt-0.5" style={{ color:"#16a34a" }}>↳ {it.external_id}</div>
                  )}
                </td>

                <td className="px-4 py-3 text-[11px] font-mono whitespace-nowrap" style={{ color:"#64748b" }}>
                  {new Date(it.queued_at).toLocaleString("en-AU")}
                </td>

                <td className="px-4 py-3 text-center">
                  <span className={`font-mono font-bold text-[12px] ${it.attempts>=5?"text-red-600":""}`}
                    style={{ color: it.attempts>=5 ? "#e31e24" : "#0f172a" }}>
                    {it.attempts||0}
                  </span>
                  <span className="font-mono text-[10px]" style={{ color:"#94a3b8" }}>
                    /{it.max_attempts||6}
                  </span>
                </td>

                <td className="px-4 py-3 text-[11px] font-mono whitespace-nowrap" style={{ color:"#64748b" }}>
                  {it.next_attempt_at && it.status==="pending"
                    ? new Date(it.next_attempt_at)<=new Date()
                      ? <span style={{ color:"#d97706", fontWeight:700 }}>Now</span>
                      : new Date(it.next_attempt_at).toLocaleString("en-AU")
                    : "—"
                  }
                </td>

                <td className="px-4 py-3">
                  <span className="text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-0.5 rounded-lg"
                    style={STATUS_STYLE[it.status]||STATUS_STYLE.pending}>
                    {it.status}
                  </span>
                  {it.last_error && (
                    <div className="text-[9px] mt-1 max-w-[180px] truncate cursor-help"
                      style={{ color:"#e31e24" }} title={it.last_error}>
                      {it.last_error}
                    </div>
                  )}
                </td>

                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1.5">
                    {["pending","failed","dead"].includes(it.status) && (
                      <Button size="sm" onClick={() => processOne(it.id)} disabled={processingId===it.id}
                        className="rounded-lg text-[11px]"
                        style={{ background:"#dbeafe", color:"#1e40af", border:"1px solid #93c5fd" }}
                        data-testid={`queue-process-${it.id}`}>
                        <Play className={`h-3 w-3 mr-1 ${processingId===it.id?"animate-pulse":""}`}/>
                        {processingId===it.id?"…":"Process"}
                      </Button>
                    )}
                    {!["sent","skipped"].includes(it.status) && (
                      <Button size="sm" onClick={() => retry(it.id)}
                        className="rounded-lg text-[11px]"
                        style={{ background:"#f5f7fb", border:"1px solid #dde3ef", color:"#475569" }}
                        data-testid={`queue-retry-${it.id}`}>
                        <RefreshCcw className="h-3 w-3 mr-1"/> Retry
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {data.items.length===0 && (
              <tr><td colSpan={7} className="p-12 text-center">
                <AlertTriangle className="h-10 w-10 mx-auto mb-3" style={{ color:"#c5cfe3" }}/>
                <div className="text-[12px] font-mono uppercase tracking-widest" style={{ color:"#94a3b8" }}>
                  No {tab==="all"?"":tab} events
                </div>
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-[11px] font-mono">
        {[
          { label:"Pending",   col:"#d97706",  desc:"queued, next auto-cycle" },
          { label:"Failed",    col:"#e31e24",  desc:"retrying (30s→5m→30m→2h→8h)" },
          { label:"Dead",      col:"#991b1b",  desc:"6 attempts exhausted — manual action" },
          { label:"Conflict",  col:"#b45309",  desc:"data conflict, see Conflicts page" },
          { label:"Sent",      col:"#16a34a",  desc:"delivered to Pinnacle" },
          { label:"Skipped",   col:"#475569",  desc:"Pinnacle-origin (loop prevention)" },
        ].map(({ label, col, desc }) => (
          <span key={label} style={{ color:"#64748b" }}>
            <span className="font-bold" style={{ color:col }}>{label}</span> — {desc}
          </span>
        ))}
      </div>
    </div>
  );
}